import {
  APIComponentType,
  APIEntityType,
  APIFieldAttributes,
  APIModelAttributes,
  APIReferenceType,
  APITagAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
  Provenance,
} from '@ardoq/api-types';
import { logError } from '@ardoq/logging';
import { FieldType } from '../types';
import { getFieldType } from './fields';
import type { EnhancedScopeData } from '@ardoq/data-model';

export const readRawValue = (
  entityType: APIEntityType,
  id: ArdoqId | number,
  fieldName: string,
  enhancedScopeData: EnhancedScopeData,
  parentEntityId?: string
) => {
  const value = getValue(
    entityType,
    id,
    fieldName,
    enhancedScopeData,
    parentEntityId
  );
  if (primitiveTypes.has(typeof value)) {
    return value as string | number | boolean;
  }

  if (!(value === null || value === undefined)) {
    const fieldType = getFieldType(
      entityType,
      id,
      fieldName,
      enhancedScopeData
    );
    if (
      fieldType === FieldType.SELECT_MULTIPLE_LIST ||
      fieldType === FieldType.COMPONENT_TYPE ||
      fieldType === FieldType.REFERENCE_TYPE ||
      fieldType === FieldType.REFERENCE_LIST ||
      fieldType === FieldType.COMPONENTS_LIST
    ) {
      return value as string[];
    } else if (fieldType === FieldType.COMPONENT_TYPE_CHILDREN) {
      return value as Record<ArdoqId, APIComponentType>;
    } else if (
      fieldType === FieldType.DATE_ONLY_RANGE ||
      fieldType === FieldType.DATE_TIME_RANGE ||
      fieldType === FieldType.DEFAULT_VALUE_DATE_ONLY_RANGE ||
      fieldType === FieldType.DEFAULT_VALUE_DATE_TIME_RANGE
    ) {
      return value as { start: string; end: string };
    } else if (
      fieldType === FieldType.CREATED_PROVENANCE ||
      fieldType === FieldType.LAST_PROVENANCE
    ) {
      return value as Provenance;
    }
    // only for dev purpose
    logError(new Error('not supported complex type'), null, {
      entityType,
      fieldType,
      id,
    });
    return value;
  }
  return null;
};

const primitiveTypes = new Set(['string', 'number', 'boolean']);

const getValue = (
  entityType: APIEntityType,
  id: ArdoqId | number,
  fieldName: string,
  enhancedScopeData: EnhancedScopeData,
  parentEntityId?: ArdoqId
) => {
  switch (entityType) {
    case APIEntityType.COMPONENT:
      return enhancedScopeData.componentsById[id]?.[fieldName];

    case APIEntityType.REFERENCE:
      return enhancedScopeData.referencesById[id]?.[fieldName];

    case APIEntityType.WORKSPACE:
      return enhancedScopeData.workspacesById[id]?.[
        fieldName as keyof APIWorkspaceAttributes
      ];

    case APIEntityType.COMPONENT_TYPE: {
      return parentEntityId
        ? enhancedScopeData.typesByModelId[parentEntityId]?.componentTypesById[
            id
          ]?.[fieldName as keyof APIComponentType]
        : undefined;
    }

    case APIEntityType.REFERENCE_TYPE: {
      return parentEntityId
        ? enhancedScopeData.typesByModelId[parentEntityId]?.referenceTypesById[
            id
          ]?.[fieldName as keyof APIReferenceType]
        : undefined;
    }

    case APIEntityType.FIELD: {
      return enhancedScopeData.fieldsById[id]?.[
        fieldName as keyof APIFieldAttributes
      ];
    }

    case APIEntityType.TAG:
      return enhancedScopeData.tagsById[id]?.[
        fieldName as keyof APITagAttributes
      ];

    case APIEntityType.MODEL:
      return enhancedScopeData.modelsById[id]?.[
        fieldName as keyof APIModelAttributes
      ];
  }
};
